import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, colors, spacing, typography } from '../components';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, BillingOverview, BillingCatalog, BillingAddon, AddonQuote, BillingCycle } from '../services/api';
import { startAddonCheckout, canPayInApp } from '../services/checkout';
import { handleApiError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useHardwareBackAction } from '../navigation/hardwareBack';
import { withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';

const priceFor = (a: BillingAddon, cycle: BillingCycle) => (cycle === 'yearly' ? a.yearPrice : cycle === 'quarterly' ? a.quartPrice : a.moPrice);

const formatMoney = (amount: number, currency: string, locale: string) => {
  try {
    return new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-US', { style: 'currency', currency, maximumFractionDigits: amount % 1 ? 2 : 0 }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

/**
 * Add-ons on top of the plan: more of a limit or a whole module, bought any
 * time and charged for the days left in the current period (everything then
 * renews together). Owned add-ons can be reduced/removed from the next
 * renewal. Pricing is always quoted by the server.
 */
export const BillingAddonsScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const { t, locale } = useLocale();
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [catalog, setCatalog] = useState<BillingCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState<Record<string, number>>({});
  const [quote, setQuote] = useState<AddonQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const quoteSeq = useRef(0);

  const goBack = () => navigateTo('subscription');
  useHardwareBackAction(goBack);
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const load = useCallback(async () => {
    try {
      const [o, c] = await Promise.all([apiService.getBillingOverview(), apiService.getBillingCatalog()]);
      if (o.statusCode === 1000 && o.data) setOverview(o.data);
      if (c.statusCode === 1000 && c.data) setCatalog(c.data);
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const plan = overview?.plan;
  const cycle = (overview?.subscription?.billingCycle || 'monthly') as BillingCycle;
  const canBuy = overview?.state === 'active' && !!overview?.canManage;
  const owned = overview?.addons || [];
  const ownedQty = (code: string) => owned.find((a) => a.code === code)?.quantity || 0;
  const nameOf = (a: BillingAddon) => a.translations?.[locale]?.name || a.name;

  // Add-ons this plan can take (module add-ons hidden when the plan already has the module).
  const available = useMemo(() => (catalog?.addons || []).filter((a) => {
    if (!plan || plan.isFree) return false;
    if (a.plans.length && !a.plans.includes(plan.code)) return false;
    if (a.kind === 'module' && overview?.entitlements?.modules?.includes(a.feature) && !ownedQty(a.code)) return false;
    if (String(a.currency).toUpperCase() !== String(plan.currency).toUpperCase()) return false;
    return true;
  }), [catalog, plan, overview]);

  const capacity = available.filter((a) => a.kind === 'quantity');
  const modules = available.filter((a) => a.kind === 'module');
  const cartItems = Object.entries(cart).filter(([, q]) => q > 0).map(([code, quantity]) => ({ code, quantity }));

  // Server quote for the current selection (debounced).
  useEffect(() => {
    if (!cartItems.length) { setQuote(null); setQuoteError(null); return; }
    const seq = ++quoteSeq.current;
    setQuoting(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiService.quoteAddons(cartItems);
        if (seq !== quoteSeq.current) return;
        if (res.statusCode === 1000 && res.data) { setQuote(res.data); setQuoteError(null); }
        else { setQuote(null); setQuoteError(res.errors?.[0]?.message || t('billing.errorGeneric')); }
      } catch (err: any) {
        if (seq === quoteSeq.current) { setQuote(null); setQuoteError(err?.message || t('billing.errorGeneric')); }
      } finally {
        if (seq === quoteSeq.current) setQuoting(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [JSON.stringify(cartItems)]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQty = (a: BillingAddon, qty: number) => {
    const cap = a.kind === 'module' ? 1 : a.maxUnits || 0;
    const room = cap > 0 ? Math.max(0, cap - ownedQty(a.code)) : 999;
    setCart((prev) => ({ ...prev, [a.code]: Math.max(0, Math.min(room, qty)) }));
  };

  const renewalExtra = cartItems.reduce((sum, i) => {
    const a = available.find((x) => x.code === i.code);
    return sum + (a ? priceFor(a, cycle) * i.quantity : 0);
  }, 0);

  const onPay = async () => {
    if (!cartItems.length) return;
    setPaying(true);
    try {
      const checkout = await startAddonCheckout(cartItems, locale === 'ar' ? 'ar' : 'en');
      if (checkout) navigateTo('billing-return', undefined, { paymentId: checkout.paymentId, from: 'billing' });
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setPaying(false);
    }
  };

  const onSchedule = async (code: string, keep: number) => {
    setSavingCode(code);
    try {
      const res = await apiService.scheduleAddon(code, keep);
      if (res.statusCode === 1000 && res.data) {
        setOverview((o) => o ? { ...o, addons: (o.addons || []).map((a) => (a.code === code ? { ...a, renewQuantity: res.data!.renewQuantity } : a)) } : o);
      } else {
        showNotification('error', t('billing.errorTitle'), res.errors?.[0]?.message || t('billing.errorGeneric'));
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setSavingCode(null);
    }
  };

  const stepper = (value: number, onChange: (v: number) => void, max?: number, disabled?: boolean) => (
    <View style={[styles.stepper, { flexDirection: rtl.rowDirection }]}>
      <TouchableOpacity style={styles.stepBtn} onPress={() => onChange(value - 1)} disabled={disabled || value <= 0}>
        <Ionicons name="remove" size={16} color={(disabled || value <= 0 ? colors.textLight : colors.primary) as string} />
      </TouchableOpacity>
      <Text style={styles.stepValue}>{value}</Text>
      <TouchableOpacity style={styles.stepBtn} onPress={() => onChange(value + 1)} disabled={disabled || (max !== undefined && value >= max)}>
        <Ionicons name="add" size={16} color={(disabled || (max !== undefined && value >= max) ? colors.textLight : colors.primary) as string} />
      </TouchableOpacity>
    </View>
  );

  const addonCard = (a: BillingAddon) => {
    const inCart = cart[a.code] || 0;
    const held = ownedQty(a.code);
    const cap = a.kind === 'module' ? 1 : a.maxUnits || 0;
    const room = cap > 0 ? Math.max(0, cap - held) : undefined;
    const current = a.kind === 'quantity' ? overview?.entitlements?.limits?.[a.feature] : undefined;
    return (
      <View key={a.code} style={[styles.card, inCart > 0 && styles.cardSelected, { flexDirection: rtl.rowDirection }]}>
        <View style={styles.cardBody}>
          <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{nameOf(a)}</Text>
          <Text style={[styles.cardMeta, { textAlign: rtl.textStart }]}>
            {formatMoney(priceFor(a, cycle), a.currency, locale)} / {t(`billing.per.${cycle}` as any)}
            {a.kind === 'quantity' && current !== undefined ? ` · ${t('billing.addons.youHave', { value: current === -1 ? '∞' : current.toLocaleString() })}` : ''}
            {a.kind === 'module' && held > 0 ? ` · ${t('billing.addons.active')}` : ''}
          </Text>
        </View>
        {canBuy && (a.kind === 'module'
          ? (held > 0 ? null : (
            <TouchableOpacity style={[styles.toggle, inCart > 0 && styles.toggleOn]} onPress={() => setQty(a, inCart > 0 ? 0 : 1)}>
              <Text style={[styles.toggleText, inCart > 0 && styles.toggleTextOn]}>{inCart > 0 ? t('billing.addons.added') : t('billing.addons.add')}</Text>
            </TouchableOpacity>
          ))
          : stepper(inCart, (v) => setQty(a, v), room))}
      </View>
    );
  };

  const ownedRows = owned.map((o) => {
    const def = catalog?.addons.find((a) => a.code === o.code);
    if (!def) return null;
    const scheduled = o.renewQuantity < o.quantity;
    return (
      <View key={o.code} style={[styles.ownedRow, { flexDirection: rtl.rowDirection }]}>
        <View style={styles.cardBody}>
          <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{nameOf(def)}{def.kind === 'quantity' ? ` × ${o.quantity}` : ''}</Text>
          <Text style={[styles.cardMeta, { textAlign: rtl.textStart }, scheduled && styles.warnText]}>
            {scheduled
              ? (o.renewQuantity === 0 ? t('billing.addons.removedAtRenewal') : t('billing.addons.reducedAtRenewal', { n: o.renewQuantity }))
              : `${formatMoney(priceFor(def, cycle) * o.quantity, def.currency, locale)} / ${t(`billing.per.${cycle}` as any)}`}
          </Text>
        </View>
        {overview?.canManage && (savingCode === o.code ? <ActivityIndicator size="small" color={colors.primary as string} /> : (
          def.kind === 'quantity'
            ? stepper(o.renewQuantity, (v) => onSchedule(o.code, v), o.quantity)
            : (
              <TouchableOpacity onPress={() => onSchedule(o.code, scheduled ? 1 : 0)} hitSlop={6}>
                <Text style={styles.linkText}>{scheduled ? t('billing.addons.keep') : t('billing.addons.removeAtRenewal')}</Text>
              </TouchableOpacity>
            )
        ))}
      </View>
    );
  });

  const blockedMessage = !overview ? null
    : overview.state === 'trial' ? t('billing.addons.afterTrial')
    : overview.state === 'free' || overview.plan?.isFree ? t('billing.addons.paidOnly')
    : overview.state === 'grace' || overview.state === 'expired' ? t('billing.addons.renewFirst')
    : overview.state === 'none' ? t('billing.addons.paidOnly')
    : !overview.canManage ? t('billing.ownerOnly')
    : null;

  return (
    <ScreenLayout
      header={<Header showNotifications showMessages onNotificationPress={() => navigateTo('notifications')} onMessagePress={() => navigateTo('chat')} showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />}
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title={t('billing.addons.title')} onBack={goBack} />}
    >
      {loading ? (
        <View style={styles.center}><ActivityIndicator size="large" color={colors.primary as string} /></View>
      ) : (
        <View style={styles.flex}>
          <ScrollView style={styles.flex} contentContainerStyle={styles.content}>
            <Text style={[styles.intro, { textAlign: rtl.textStart }]}>{t('billing.addons.intro')}</Text>

            {blockedMessage && (
              <View style={[styles.notice, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="information-circle-outline" size={18} color={colors.textLight as string} />
                <Text style={[styles.noticeText, { textAlign: rtl.textStart }]}>{blockedMessage}</Text>
                {(overview?.state === 'trial' || overview?.state === 'free' || overview?.state === 'none') && overview?.canManage && (
                  <TouchableOpacity onPress={() => navigateTo('plan-selection', undefined, { mode: 'change' })}>
                    <Text style={styles.linkText}>{t('billing.choosePlan')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}

            {owned.length > 0 && (
              <>
                <Text style={[styles.section, { textAlign: rtl.textStart }]}>{t('billing.addons.yours')}</Text>
                <View style={styles.group}>{ownedRows}</View>
              </>
            )}

            {capacity.length > 0 && (
              <>
                <Text style={[styles.section, { textAlign: rtl.textStart }]}>{t('billing.addons.capacity')}</Text>
                <View style={styles.list}>{capacity.map(addonCard)}</View>
              </>
            )}
            {modules.length > 0 && (
              <>
                <Text style={[styles.section, { textAlign: rtl.textStart }]}>{t('billing.addons.modules')}</Text>
                <View style={styles.list}>{modules.map(addonCard)}</View>
              </>
            )}
            {!blockedMessage && !capacity.length && !modules.length && (
              <Text style={[styles.intro, { textAlign: rtl.textStart }]}>{t('billing.addons.none')}</Text>
            )}
          </ScrollView>

          {cartItems.length > 0 && (
            <View style={[styles.footer, { flexDirection: rtl.rowDirection }]}>
              <View style={styles.cardBody}>
                {quoting ? <ActivityIndicator size="small" color={colors.primary as string} /> : quote ? (
                  <>
                    <Text style={[styles.footerTotal, { textAlign: rtl.textStart }]}>
                      {t('billing.addons.payToday', { amount: formatMoney(quote.total, quote.currency, locale), days: quote.daysLeft })}
                    </Text>
                    <Text style={[styles.cardMeta, { textAlign: rtl.textStart }]}>
                      {t('billing.addons.thenEach', { amount: formatMoney(renewalExtra, quote.currency, locale), period: t(`billing.per.${cycle}` as any) })}
                    </Text>
                  </>
                ) : <Text style={[styles.warnText, { textAlign: rtl.textStart }]}>{quoteError}</Text>}
              </View>
              {canPayInApp() ? (
                <TouchableOpacity style={[styles.payBtn, (!quote || paying) && styles.disabled]} onPress={onPay} disabled={!quote || paying}>
                  {paying ? <ActivityIndicator color={colors.literal.white as string} /> : <Text style={styles.payText}>{t('billing.payNow')}</Text>}
                </TouchableOpacity>
              ) : (
                <Text style={styles.cardMeta}>{t('billing.unavailableTitle')}</Text>
              )}
            </View>
          )}
        </View>
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  flex: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  content: { padding: spacing.md, paddingBottom: spacing.xl * 3, gap: spacing.sm, maxWidth: 760, width: '100%', alignSelf: 'center' },
  intro: { fontSize: typography.fontSize.sm, color: colors.textLight },
  notice: { alignItems: 'center', gap: spacing.sm, padding: spacing.md, borderRadius: designTokens.borderRadius.md, backgroundColor: colors.background.secondary },
  noticeText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textDark },
  section: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.textDark, marginTop: spacing.md },
  group: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, paddingHorizontal: spacing.md },
  list: { gap: spacing.sm },
  card: { alignItems: 'center', gap: spacing.sm, padding: spacing.md, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  cardSelected: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary, 0.05) },
  cardBody: { flex: 1, minWidth: 0, gap: 2 },
  cardTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  cardMeta: { fontSize: typography.fontSize.xs, color: colors.textLight },
  warnText: { color: colors.status.warning, fontSize: typography.fontSize.xs },
  ownedRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  stepper: { alignItems: 'center', gap: 6, borderWidth: 1, borderColor: colors.border, borderRadius: 10, paddingHorizontal: 4 },
  stepBtn: { padding: 6 },
  stepValue: { minWidth: 20, textAlign: 'center', fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  toggle: { borderWidth: 1, borderColor: colors.primary, borderRadius: 10, paddingVertical: 6, paddingHorizontal: spacing.md },
  toggleOn: { backgroundColor: colors.primary },
  toggleText: { color: colors.primary, fontFamily: typography.fontFamily.semibold, fontSize: typography.fontSize.sm },
  toggleTextOn: { color: colors.literal.white },
  linkText: { color: colors.primary, fontFamily: typography.fontFamily.semibold, fontSize: typography.fontSize.sm },
  footer: {
    position: 'absolute', left: 0, right: 0, bottom: 0, alignItems: 'center', gap: spacing.md,
    padding: spacing.md, borderTopWidth: 1, borderTopColor: colors.border, backgroundColor: colors.background.surface,
  },
  footerTotal: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.bold, color: colors.textDark },
  payBtn: { backgroundColor: colors.primary, borderRadius: 10, paddingVertical: 10, paddingHorizontal: spacing.lg, minWidth: 110, alignItems: 'center' },
  payText: { color: colors.literal.white, fontFamily: typography.fontFamily.semibold },
  disabled: { opacity: 0.5 },
});
